import {AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';

/**
 * The dashboard's PDF report (ITP-E-146): what each card gives the report, and the report's sections. The cards
 * register with AdminDashboardReportRegistry; AdminDashboardReportPdfService draws the result.
 */
export type AdminDashboardReportSectionKey =
  'attention' | 'users-adoption' | 'platform-usage' | 'licences-revenue' | 'deeper-analysis' | 'product-quality';

export interface AdminDashboardReportColumn {
  header: string;
  /** Default 'left'. */
  align?: 'left' | 'right';
  /** Share of the table width; default 1. */
  weight?: number;
}

export interface AdminDashboardReportRow {
  cells: string[];
  /** Optional status stripe on the left edge. */
  tone?: 'red' | 'amber' | 'green';
}

export interface AdminDashboardReportStat {
  label: string;
  value: string;
  detail?: string;
}

interface AdminDashboardReportBlockBase {
  title: string;
  subtitle?: string;
  note?: string;
}

export type AdminDashboardReportBlock =
  | (AdminDashboardReportBlockBase & {kind: 'table'; columns: AdminDashboardReportColumn[];
      rows: AdminDashboardReportRow[]; emptyText?: string})
  | (AdminDashboardReportBlockBase & {kind: 'stats'; stats: AdminDashboardReportStat[]})
  | (AdminDashboardReportBlockBase & {kind: 'chart'; image: string | null; widthPx: number; heightPx: number;
      emptyText?: string})
  | (AdminDashboardReportBlockBase & {kind: 'empty'; text: string});

/** A dashboard card that can describe itself for the PDF report. */
export interface AdminDashboardReportProvider {
  /** The card's last reported state; the report leaves out an 'empty' card, as the dashboard hides it (ITP-E-173-01). */
  readonly widgetState?: AdminDashboardWidgetState;
  /** Blocks for the report, built from what the card shows now (never reloads data). [] = leave the card out. */
  reportBlocks(): AdminDashboardReportBlock[];
}

export interface AdminDashboardReportSection {
  key: AdminDashboardReportSectionKey;
  title: string;
  subtitle: string | null;
  blocks: AdminDashboardReportBlock[];
}

export interface AdminDashboardReportHeader {
  scopeLabel: string;
  periodLabel: string;
  filtersLabel: string;
  generatedAt: Date;
}

export const ADMIN_DASHBOARD_REPORT_SECTIONS: {key: AdminDashboardReportSectionKey; title: string; subtitle: string | null}[] = [
  {key: 'attention', title: 'Needs attention', subtitle: null},
  {key: 'users-adoption', title: 'Users & adoption', subtitle: 'Who is using the platform, and how much of what they bought.'},
  {key: 'platform-usage', title: 'Projects, scenarios & entities', subtitle: 'What customers build on the platform, and where it is heading.'},
  {key: 'licences-revenue', title: 'Licences & revenue', subtitle: 'What renews, what ends, and what was billed.'},
  {key: 'deeper-analysis', title: 'Deeper analysis', subtitle: 'Whether new companies stay, and how trials turn into customers.'},
  {key: 'product-quality', title: 'Product quality', subtitle: 'Where XatBot falls short, and where signing in fails.'}
];
